import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  from,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  takeWhile,
  tap,
  throwError,
  timer
} from 'rxjs';
import { BangumiProviderService } from '../../bangumi-provider.service';
import { BgmAccountInfo } from '../../../sdk/bangumiProvider';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorMessageComponent } from '../../ui/error-message/error-message.component';
import { UIDialog, UIToast, UIToastComponent, UIToastRef, UIToggle } from '@irohalab/deneb-ui';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { extractMessage } from '../../utils/error.util';
import { ImportBgmDialogComponent } from './import-bgm-dialog/import-bgm-dialog.component';

const PROGRESS_POLL_INTERVAL_MS = 10000;
const MIN_PROGRESS_POLLS = 6;
const MAX_PROGRESS_POLLS = 180;

@Component({
    selector: 'app-bgm-account-settings',
    imports: [
    NgClass,
    ErrorMessageComponent,
    UIToggle,
    ReactiveFormsModule
],
    templateUrl: './bgm-account-settings.component.html',
    styleUrl: './bgm-account-settings.component.less'
})
export class BgmAccountSettingsComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private toastRef: UIToastRef<UIToastComponent>;

  private _bgmAccount: BgmAccountInfo | null = null;
  get bgmAccount(): BgmAccountInfo | null {
    return this._bgmAccount;
  }

  set bgmAccount(value: BgmAccountInfo | null) {
    this._bgmAccount = value;
    this.bgmAccountSettingForm.patchValue({
      enableAutoSync: !!this._bgmAccount?.autoSync
    });
  }

  loading = true;
  linking = false;
  callbackLoading = false;
  syncPolling = false;
  unlinking = false;
  error: HttpErrorResponse|null = null;

  bgmAccountSettingForm!: FormGroup;

  get profileUrl(): string {
    return `https://bgm.tv/user/${this.bgmAccount?.username}`;
  }

  constructor(private bangumiProviderService: BangumiProviderService,
              private routeParams: ActivatedRoute,
              private router: Router,
              private fb: FormBuilder,
              private dialog: UIDialog,
              uiToast: UIToast,
              @Inject(PLATFORM_ID) private platformId: Object) {
    this.toastRef = uiToast.makeText();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.bgmAccountSettingForm = this.fb.group({
      enableAutoSync: true,
    })
    this.subscription.add(
      this.routeParams.queryParams
        .pipe(
          take(1),
          switchMap((params) => {
            const code = params['code'];
            const state = params['state'];
            if (code && state) {
              this.loading = false;
              this.callbackLoading = true;
              if (!isPlatformBrowser(this.platformId)) {
                return EMPTY;
              }
              return from(this.router.navigate([], {
                relativeTo: this.routeParams,
                queryParams: {code: null, state: null},
                queryParamsHandling: 'merge',
                replaceUrl: true
              })).pipe(
                switchMap(() => this.bangumiProviderService.bgmAuthCallback(code, state)),
                switchMap(() => this.loadAccount()),
                tap(() => this.openImportDialog()),
                map(() => false)
              );
            }

            return this.loadAccount()
              .pipe(
                map(() => {
                  this.loading = false;
                  return false;
                }),
                catchError((error: HttpErrorResponse) => {
                  this.loading = false;
                  if (error.status === 404) {
                    this.bgmAccount = null;
                    return of(false);
                  } else {
                    return throwError(() => error);
                  }
                })
              );
          }))
        .subscribe({
          next: () => {
            this.callbackLoading = false;
          },
          error: (error: HttpErrorResponse) => {
            this.callbackLoading = false;
            this.error = error;
          }
        })
    );
  }

  linkAccount(): void {
    this.linking = true;
    this.subscription.add(
      this.bangumiProviderService.getBgmAuthUrl()
        .subscribe({
          next: (resp) => {
            if (isPlatformBrowser(this.platformId)) {
              window.location.href = resp.data;
            }
          },
          error: (err: HttpErrorResponse) => {
            console.log(err);
            this.linking = false;
            this.error = err;
          }
        })
    );
  }

  unlinkAccount(): void {
    this.unlinking = true;
    this.subscription.add(
      this.bangumiProviderService.deleteBgmAccount()
        .subscribe({
          next: () => {
            this.bgmAccount = null;
            this.unlinking = false;
          },
          error: (err: HttpErrorResponse) => {
            console.log(err);
            this.unlinking = false;
            this.error = err;
          }
        })
    );
  }

  onAutoSyncToggled(): void {
    const autoSync = Boolean(this.bgmAccountSettingForm.value.enableAutoSync);
    this.subscription.add(
      this.bangumiProviderService.updateBgmSyncSettings(autoSync)
        .subscribe({
          next: () => {
            this.toastRef.show('Update autoSync success!');
          },
          error: (err: HttpErrorResponse) => {
            this.bgmAccountSettingForm.get('enableAutoSync')?.setValue(!autoSync, { emitEvent: false });
            this.toastRef.show(extractMessage(err));
          }
        })
    );
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open<ImportBgmDialogComponent>(ImportBgmDialogComponent, {
      stickyDialog: true,
      backdrop: true
    });
    this.subscription.add(
      dialogRef.afterClosed().pipe(
        filter((importStarted) => importStarted === true),
        tap(() => {
          this.syncPolling = true;
        }),
        switchMap(() => this.watchSyncProgress()),
        finalize(() => {
          this.syncPolling = false;
        })
      ).subscribe({
        error: (err: HttpErrorResponse) => {
          this.toastRef.show(extractMessage(err));
        }
      })
    );
  }

  private loadAccount(): Observable<BgmAccountInfo> {
    return this.bangumiProviderService.getBgmAccount().pipe(
      tap((account) => {
        this.bgmAccount = account;
      })
    );
  }

  private watchSyncProgress(): Observable<BgmAccountInfo> {
    return timer(0, PROGRESS_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.loadAccount()),
      takeWhile((account, index) => {
        const importPending = (account.importTotal ?? 0) > (account.importProgress ?? 0);
        const exportPending = (account.exportTotal ?? 0) > (account.exportProgress ?? 0);
        return index < MIN_PROGRESS_POLLS - 1 || importPending || exportPending;
      }, true),
      take(MAX_PROGRESS_POLLS)
    );
  }
}
